import { useContext, useEffect, useState } from "react";
import {
  Camera,
  Check,
  Facebook,
  Globe,
  Image as ImageIcon,
  Instagram,
  Loader2,
  MapPin,
  MessageCircle,
  Phone,
  Send,
  Trash2,
  Upload,
} from "lucide-react";
import { toast } from "react-toastify";
import { UserContext } from "../context/UserContext";
import { FoodServiceContext } from "../context/FoodServiceContext";
import API_URL from "../config/apiConfig";

const DEFAULT_COVERS = Array.from({ length: 20 }, (_, index) => `/images/default-covers/cover-${index + 1}.jpg`);

const emptyProfile = {
  name: "",
  phone: "",
  address: "",
  showAddress: true,
  description: "",
  openingHours: "",
  welcomeText: "",
  instagram: "",
  whatsapp: "",
  telegram: "",
  facebook: "",
  website: "",
  profileImage: { file: null, url: null },
  coverImage: { file: null, url: null },
  gallery: [],
};

const Field = ({ label, icon: Icon, children }) => (
  <label className="block">
    <span className="mb-1.5 flex items-center gap-1.5 text-xs font-bold text-gray-600">
      {Icon && <Icon size={13} aria-hidden="true" />}
      {label}
    </span>
    {children}
  </label>
);

const SectionLabel = ({ children }) => (
  <p className="text-[11px] font-bold uppercase tracking-wide text-gray-400">{children}</p>
);

const inputClass = "min-h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-accent outline-none focus:border-primary focus:ring-4 focus:ring-primary/10";

export default function RestaurantProfile() {
  const { user } = useContext(UserContext);
  const { setRestaurantData } = useContext(FoodServiceContext);
  const userId = user?._id;

  const [profile, setProfile] = useState(emptyProfile);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCoverPicker, setShowCoverPicker] = useState(false);

  useEffect(() => {
    if (!userId) return;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/restaurantSettings/${userId}/settings`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        const data = await res.json();
        const s = data.restaurantSettings || {};
        setProfile({
          name: data.name || "",
          phone: data.phone || "",
          address: s.address || "",
          showAddress: s.showAddress ?? true,
          description: s.description || "",
          openingHours: s.openingHours || "",
          welcomeText: s.welcomeText || "",
          instagram: s.socialLinks?.instagram || "",
          whatsapp: s.socialLinks?.whatsapp || "",
          telegram: s.socialLinks?.telegram || "",
          facebook: s.socialLinks?.facebook || "",
          website: s.socialLinks?.website || "",
          profileImage: { file: null, url: s.profileImage ? `${API_URL}/uploads/restaurants/${userId}/${s.profileImage}` : null },
          coverImage: {
            file: null,
            url: s.coverImage
              ? s.coverImage.startsWith("cover-") ? `/images/default-covers/${s.coverImage}` : `${API_URL}/uploads/restaurants/${userId}/${s.coverImage}`
              : null,
          },
          gallery: (s.gallery || []).map((img) => ({ file: null, url: `${API_URL}/uploads/restaurants/${userId}/${img}` })),
        });
      } catch {
        toast.error("دریافت اطلاعات پروفایل ناموفق بود.");
      } finally {
        setLoading(false);
      }
    })();
  }, [userId]);

  const set = (field, value) => setProfile((prev) => ({ ...prev, [field]: value }));

  const handleImageFile = (field, file) => set(field, { file, url: file ? URL.createObjectURL(file) : null });

  const handleGalleryAdd = (files) => {
    const added = Array.from(files).map((file) => ({ file, url: URL.createObjectURL(file) }));
    set("gallery", [...profile.gallery, ...added]);
  };

  const removeGalleryImage = (index) => set("gallery", profile.gallery.filter((_, i) => i !== index));

  const handleSave = async () => {
    if (!userId) return;
    setSaving(true);
    try {
      const formData = new FormData();
      if (profile.profileImage.file) formData.append("profileImage", profile.profileImage.file);
      else if (!profile.profileImage.url) formData.append("profileImage", "");

      if (profile.coverImage.file) formData.append("coverImage", profile.coverImage.file);
      else if (!profile.coverImage.url) formData.append("coverImage", "");
      else if (profile.coverImage.url.startsWith("/images/default-covers/")) formData.append("coverImage", profile.coverImage.url.split("/").pop());

      formData.append("name", profile.name);
      formData.append("phone", profile.phone);
      formData.append("address", profile.address);
      formData.append("showAddress", profile.showAddress);
      formData.append("description", profile.description);
      formData.append("openingHours", profile.openingHours);
      formData.append("welcomeText", profile.welcomeText);
      formData.append("instagram", profile.instagram);
      formData.append("whatsapp", profile.whatsapp);
      formData.append("telegram", profile.telegram);
      formData.append("facebook", profile.facebook);
      formData.append("website", profile.website);

      profile.gallery.forEach((img) => {
        if (img.file) formData.append("gallery", img.file);
        else formData.append("existingGallery", img.url);
      });

      const res = await fetch(`${API_URL}/api/restaurantSettings/${userId}/settings`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        body: formData,
      });
      const data = await res.json();

      if (!res.ok) {
        toast.error(data?.error?.message || data?.message || "ذخیره پروفایل ناموفق بود.");
        return;
      }

      if (data.restaurant) {
        setRestaurantData((prev) => (prev ? { ...prev, ...data.restaurant } : data.restaurant));
        localStorage.setItem(`restaurantData_${userId}`, JSON.stringify(data.restaurant));
      }
      toast.success("پروفایل با موفقیت ذخیره شد");
    } catch {
      toast.error("ذخیره پروفایل ناموفق بود.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="mx-auto w-full max-w-3xl p-4 sm:p-5">
        <div className="h-6 w-32 animate-pulse rounded-lg bg-gray-100" />
        <div className="mt-4 h-64 animate-pulse rounded-2xl bg-gray-100" />
      </div>
    );
  }

  return (
    <main className="mx-auto w-full max-w-3xl p-4 sm:p-5 lg:p-6" dir="rtl">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-black text-accent">پروفایل رستوران</h1>
          <p className="mt-1 text-xs leading-5 text-gray-500">هویت، برند و اطلاعاتی که مشتریان شما در منوی آنلاین می‌بینند.</p>
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="inline-flex h-10 shrink-0 items-center justify-center gap-1.5 rounded-lg bg-primary px-4 text-sm font-bold text-white transition-colors hover:bg-[#e8731f] disabled:cursor-wait disabled:opacity-60 focus:outline-none focus:ring-4 focus:ring-primary/20"
        >
          {saving ? <Loader2 size={15} className="animate-spin" aria-hidden="true" /> : <Check size={15} aria-hidden="true" />}
          {saving ? "در حال ذخیره..." : "ذخیره پروفایل"}
        </button>
      </header>

      <div className="mt-4 overflow-hidden rounded-2xl border border-gray-200 bg-white">
        {/* Cover */}
        <div className="relative h-36 bg-gray-100 sm:h-44">
          {profile.coverImage.url ? (
            <img src={profile.coverImage.url} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full items-center justify-center text-gray-300"><ImageIcon size={28} aria-hidden="true" /></div>
          )}
          <button
            type="button"
            onClick={() => setShowCoverPicker(true)}
            className="absolute bottom-3 left-3 inline-flex h-9 items-center gap-1.5 rounded-lg bg-white/95 px-3 text-xs font-bold text-accent shadow hover:bg-white"
          >
            <Camera size={13} aria-hidden="true" />
            تغییر سربرگ
          </button>

          {/* Avatar — overlaps the cover's bottom edge, click to change */}
          <label className="group absolute -bottom-10 right-6 flex h-20 w-20 cursor-pointer items-center justify-center overflow-hidden rounded-2xl border-4 border-white bg-gray-100 shadow-md sm:h-24 sm:w-24">
            {profile.profileImage.url ? (
              <img src={profile.profileImage.url} alt="" className="h-full w-full object-cover" />
            ) : (
              <ImageIcon className="text-gray-300" size={24} aria-hidden="true" />
            )}
            <span className="absolute inset-0 flex items-center justify-center bg-black/0 text-transparent transition-colors group-hover:bg-black/40 group-hover:text-white">
              <Camera size={18} aria-hidden="true" />
            </span>
            <input type="file" accept="image/*" className="hidden" onChange={(event) => handleImageFile("profileImage", event.target.files[0])} />
          </label>
        </div>

        {/* Form — one flowing card, no nested boxes */}
        <div className="space-y-6 px-5 pb-6 pt-14 sm:px-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="نام رستوران">
              <input value={profile.name} onChange={(event) => set("name", event.target.value)} className={inputClass} />
            </Field>
            <Field label="شماره تماس" icon={Phone}>
              <input value={profile.phone} onChange={(event) => set("phone", event.target.value)} dir="ltr" className={inputClass} />
            </Field>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-xs font-bold text-gray-600"><MapPin size={13} aria-hidden="true" /> آدرس</span>
              <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500">
                <input type="checkbox" checked={profile.showAddress} onChange={(event) => set("showAddress", event.target.checked)} className="h-3.5 w-3.5 rounded" />
                نمایش به مشتریان
              </label>
            </div>
            <textarea value={profile.address} onChange={(event) => set("address", event.target.value)} rows={2} className={`${inputClass} mt-1.5 min-h-0 py-2`} />
          </div>

          <Field label="ساعات کاری">
            <input value={profile.openingHours} onChange={(event) => set("openingHours", event.target.value)} placeholder="مثلاً هر روز ۱۲ تا ۲۳" className={`${inputClass} sm:max-w-xs`} />
          </Field>

          <Field label="توضیحات رستوران">
            <textarea value={profile.description} onChange={(event) => set("description", event.target.value)} rows={2} className={`${inputClass} min-h-0 py-2`} />
          </Field>

          <Field label="متن خوش‌آمدگویی (در منوی آنلاین نمایش داده می‌شود)">
            <textarea value={profile.welcomeText} onChange={(event) => set("welcomeText", event.target.value)} rows={2} className={`${inputClass} min-h-0 py-2`} />
          </Field>

          <div className="space-y-3 border-t border-gray-100 pt-5">
            <SectionLabel>شبکه‌های اجتماعی</SectionLabel>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="اینستاگرام" icon={Instagram}>
                <input value={profile.instagram} onChange={(event) => set("instagram", event.target.value)} dir="ltr" placeholder="نام کاربری یا لینک" className={inputClass} />
              </Field>
              <Field label="واتساپ" icon={MessageCircle}>
                <input value={profile.whatsapp} onChange={(event) => set("whatsapp", event.target.value)} dir="ltr" className={inputClass} />
              </Field>
              <Field label="تلگرام" icon={Send}>
                <input value={profile.telegram} onChange={(event) => set("telegram", event.target.value)} dir="ltr" placeholder="نام کاربری یا لینک" className={inputClass} />
              </Field>
              <Field label="فیسبوک" icon={Facebook}>
                <input value={profile.facebook} onChange={(event) => set("facebook", event.target.value)} dir="ltr" className={inputClass} />
              </Field>
              <Field label="وبسایت" icon={Globe}>
                <input value={profile.website} onChange={(event) => set("website", event.target.value)} dir="ltr" placeholder="https://" className={inputClass} />
              </Field>
            </div>
          </div>

          <div className="space-y-3 border-t border-gray-100 pt-5">
            <SectionLabel>گالری تصاویر</SectionLabel>
            <div className="flex flex-wrap gap-2.5">
              {profile.gallery.map((img, index) => (
                <div key={img.url} className="group relative h-20 w-20 overflow-hidden rounded-lg border border-gray-200">
                  <img src={img.url} alt="" className="h-full w-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeGalleryImage(index)}
                    aria-label="حذف تصویر"
                    className="absolute inset-0 flex items-center justify-center bg-black/50 text-white opacity-0 transition-opacity group-hover:opacity-100"
                  >
                    <Trash2 size={16} aria-hidden="true" />
                  </button>
                </div>
              ))}
              <label className="flex h-20 w-20 cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-gray-300 text-gray-400 hover:border-primary/40 hover:text-primary">
                <Upload size={16} aria-hidden="true" />
                <span className="text-[10px] font-bold">افزودن</span>
                <input type="file" accept="image/*" multiple className="hidden" onChange={(event) => handleGalleryAdd(event.target.files)} />
              </label>
            </div>
          </div>
        </div>
      </div>

      {showCoverPicker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowCoverPicker(false)}>
          <div className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-5" onClick={(event) => event.stopPropagation()}>
            <h2 className="mb-4 text-base font-black text-accent">انتخاب سربرگ پیشنهادی</h2>
            <div className="grid grid-cols-3 gap-2.5">
              {DEFAULT_COVERS.map((src) => (
                <button
                  key={src}
                  type="button"
                  onClick={() => { set("coverImage", { file: null, url: src }); setShowCoverPicker(false); }}
                  className="overflow-hidden rounded-lg border border-gray-200 transition-transform hover:scale-105"
                >
                  <img src={src} alt="" className="h-16 w-full object-cover" />
                </button>
              ))}
            </div>
            <div className="mt-4 flex items-center justify-between border-t border-gray-100 pt-4">
              <label className="inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-lg border border-gray-200 px-3 text-xs font-bold text-accent hover:bg-gray-50">
                <Upload size={13} aria-hidden="true" />
                آپلود از دستگاه
                <input type="file" accept="image/*" className="hidden" onChange={(event) => { handleImageFile("coverImage", event.target.files[0]); setShowCoverPicker(false); }} />
              </label>
              <button type="button" onClick={() => setShowCoverPicker(false)} className="text-xs font-bold text-gray-500 hover:text-accent">بستن</button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
